/* Las cuentas de la pantalla de gasoil de la base. Puras, para poder fijarlas
 * en tests sin montar nada.
 *
 * ── LO QUE DICE EL PAPEL Y LO QUE SE PUDO RESOLVER ─────────────────────────
 *
 * Cada carga trae el chofer y la patente COMO LOS DICE EL VALE (`chofer`,
 * `patente`) y, aparte, lo que el bot pudo resolver sin dudas contra el padrón
 * (`chofer_padron`, `interno` + `unidad_tipo`). Lo resuelto manda para
 * agrupar —«BAZAN G.» y «BAZAN GABRIEL» son la misma persona si el padrón lo
 * dice—, y cuando no hay resolución se agrupa por el texto del papel: una
 * patente que no es de la flota (un auto, un tercero) sigue siendo una unidad
 * a la que se le cargó gasoil, y esconderla sería esconder litros.
 */

import { diaIsoAr } from "./fechaAr";

const pelar = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();

const TIPO = { tractor: "Tractor", furgon: "Furgón", utilitario: "Utilitario" };

/* El chofer a mostrar: el del padrón si se resolvió, si no el del papel. */
export const choferDe = (c) =>
  (c?.chofer_padron || c?.chofer || "").trim();

/* La unidad, en palabras: «Tractor 1012 · AB560LD», o la patente sola cuando
 * no es de la flota. */
export function unidadDe(c) {
  const pat = String(c?.patente || "").trim().toUpperCase();
  if (c?.interno) {
    const tipo = TIPO[c.unidad_tipo] || "Interno";
    return pat ? `${tipo} ${c.interno} · ${pat}` : `${tipo} ${c.interno}`;
  }
  // El utilitario está en el padrón pero no tiene interno: se dice qué es.
  if (c?.unidad_tipo && TIPO[c.unidad_tipo] && pat) return `${TIPO[c.unidad_tipo]} · ${pat}`;
  return pat;
}

/* Si la patente del vale NO es de ningún padrón de la flota: un auto, un
 * tercero. No es un error —el vale se le llena a quien venga— pero se dice. */
export const esPatenteLibre = (c) => Boolean(c?.patente) && !c?.unidad_tipo;

/* Las claves de agrupación. `null` = esa carga no tiene el dato, y se junta
 * en un renglón «sin …» en vez de desaparecer: los litros tienen que sumar lo
 * mismo agrupados de cualquier forma. */
const CLAVES = {
  dia: (c) => diaIsoAr(c.fecha) || null,
  mes: (c) => (diaIsoAr(c.fecha) || "").slice(0, 7) || null,
  chofer: (c) => pelar(choferDe(c)) || null,
  unidad: (c) =>
    c.interno
      ? `${c.unidad_tipo || "interno"}:${c.interno}`
      : pelar(c.patente).replace(/[^A-Z0-9]/g, "") || null,
};

const SIN = { dia: "sin fecha", mes: "sin fecha", chofer: "sin chofer", unidad: "sin unidad" };

export const AGRUPACIONES = [
  { id: "dia", label: "Día" },
  { id: "mes", label: "Mes" },
  { id: "chofer", label: "Chofer" },
  { id: "unidad", label: "Unidad" },
];

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio",
  "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const rotuloDia = (k) => `${k.slice(8, 10)}/${k.slice(5, 7)}/${k.slice(0, 4)}`;
const rotuloMes = (k) => `${MESES[Number(k.slice(5, 7)) - 1] || k.slice(5, 7)} ${k.slice(0, 4)}`;

/* Total de cargas y litros por día, mes, chofer o unidad.
 *
 * Día y mes van del más nuevo al más viejo (como la tabla); chofer y unidad,
 * del que más litros se llevó al que menos — es la pregunta que se hace el
 * que agrupa por persona. El renglón «sin …» va siempre último. */
export function totales(filas, por = "dia") {
  const clave = CLAVES[por] || CLAVES.dia;
  const grupos = new Map();
  for (const c of filas || []) {
    const k = clave(c);
    if (!grupos.has(k)) {
      let rotulo = SIN[por] || "sin dato";
      if (k !== null) {
        if (por === "dia") rotulo = rotuloDia(k);
        else if (por === "mes") rotulo = rotuloMes(k);
        else if (por === "chofer") rotulo = choferDe(c);
        else rotulo = unidadDe(c);
      }
      grupos.set(k, { clave: k, rotulo, cargas: 0, litros: 0 });
    }
    const g = grupos.get(k);
    g.cargas += 1;
    g.litros += Number(c.litros) || 0;
  }
  const lista = [...grupos.values()].map((g) => ({
    ...g, litros: Math.round(g.litros * 100) / 100,
  }));
  const conDato = lista.filter((g) => g.clave !== null);
  const sinDato = lista.filter((g) => g.clave === null);
  if (por === "dia" || por === "mes") {
    conDato.sort((a, b) => (a.clave < b.clave ? 1 : a.clave > b.clave ? -1 : 0));
  } else {
    conDato.sort((a, b) => b.litros - a.litros || a.rotulo.localeCompare(b.rotulo));
  }
  return [...conDato, ...sinDato];
}

/* El buscador de la pantalla: chofer (del papel o del padrón), patente,
 * interno, vale o lo escrito a mano. Sin tildes ni mayúsculas: «bazan»
 * encuentra a «BAZÁN». */
export function filtrar(filas, texto) {
  const q = pelar(texto);
  if (!q) return filas || [];
  const qSinEsp = q.replace(/[^A-Z0-9]/g, "");
  return (filas || []).filter((c) => {
    const campos = [c.chofer, c.chofer_padron, c.interno, c.vale, c.referencia]
      .map(pelar);
    if (campos.some((x) => x && x.includes(q))) return true;
    const pat = pelar(c.patente).replace(/[^A-Z0-9]/g, "");
    return Boolean(qSinEsp) && pat.includes(qSinEsp);
  });
}

/* «283281 283283 a 283285» → [283281, 283283, 283284, 283285]. Para el
 * formulario de anular: se escribe como en el bot (`/anular`), con «a» para
 * los rangos. El rango se topa en 500 —lo mismo que la base— para que un
 * dedazo no anote medio talonario. */
export function numerosDe(texto) {
  const s = String(texto ?? "");
  const out = new Set();
  const resto = s.replace(/(\d+)\s*(?:a|al|hasta|-)\s*(\d+)/gi, (_, a, b) => {
    const ini = Number(a);
    const fin = Number(b);
    if (ini <= fin && fin - ini <= 500) {
      for (let n = ini; n <= fin; n++) out.add(n);
    }
    return " ";
  });
  (resto.match(/\d+/g) || []).forEach((n) => out.add(Number(n)));
  return [...out].filter((n) => n > 0).sort((a, b) => a - b);
}
